import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { calculateDilution, calculateStockAmount } from '../../utils/DilutionCalculator';

// For easier use with Material UI Grid
const GridItem = ({ children, xs, md, size, ...props }: any) => {
  if (size) {
    return <Grid size={size} {...props}>{children}</Grid>;
  }
  return <Grid size={{ xs, md }} {...props}>{children}</Grid>;
};

interface FormData {
  stockEC: number;
  targetEC: number;
  waterEC: number;
  containerVolume: number;
  stockSolutionVolume: number;
  calculationType: 'ratio' | 'amount';
}

interface DilutionResult {
  stockSolutionAmount?: number;
  waterAmount?: number;
  finalEC?: number;
  dilutionRatio?: string;
  error?: string;
}

const WorkingSolutionCalculator: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    stockEC: 2.0,
    targetEC: 0.5,
    waterEC: 0.0,
    containerVolume: 10,
    stockSolutionVolume: 1,
    calculationType: 'ratio'
  });
  
  const [result, setResult] = useState<DilutionResult | null>(null);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: Number(value)
    });
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleCalculate = () => {
    try {
      if (formData.calculationType === 'ratio') {
        const dilutionResult = calculateDilution({
          stockEC: formData.stockEC,
          targetEC: formData.targetEC,
          waterEC: formData.waterEC,
          stockSolutionVolume: formData.stockSolutionVolume,
          containerVolume: formData.containerVolume
        });
        
        setResult(dilutionResult);
      } else {
        const stockAmount = calculateStockAmount(
          formData.targetEC,
          formData.stockEC,
          formData.containerVolume,
          formData.waterEC
        );
        
        setResult({
          stockSolutionAmount: stockAmount,
          waterAmount: formData.containerVolume - (stockAmount / 1000),
          finalEC: formData.targetEC,
          dilutionRatio: 'Custom'
        });
      }
    } catch (error: any) {
      setResult({ error: error.message });
    }
  };
  
  return (
    <Card>
      <CardHeader 
        title="Working Solution Calculator"
        subheader="Calculate dilution ratios and amounts for your nutrient solutions"
      />
      <CardContent>
        <Grid container spacing={3}>
          <GridItem xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="calculation-type-label">Calculation Type</InputLabel>
                <Select
                  labelId="calculation-type-label"
                  name="calculationType"
                  value={formData.calculationType}
                  onChange={handleSelectChange}
                  label="Calculation Type"
                >
                  <MenuItem value="ratio">Calculate Dilution Ratio</MenuItem>
                  <MenuItem value="amount">Calculate Stock Solution Amount</MenuItem>
                </Select>
              </FormControl>
              
              <Typography variant="subtitle1" gutterBottom>Solution Parameters</Typography>
              
              <Grid container spacing={2}>
                <GridItem xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="stockEC"
                    label="Stock Solution EC"
                    type="number"
                    value={formData.stockEC}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: <Typography variant="caption">mS/cm</Typography>
                    }}
                    inputProps={{ min: 0.1, step: 0.1 }}
                    margin="normal"
                  />
                </GridItem>
                
                <GridItem xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="targetEC"
                    label="Target EC"
                    type="number"
                    value={formData.targetEC}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: <Typography variant="caption">mS/cm</Typography>
                    }}
                    inputProps={{ min: 0.1, step: 0.1 }}
                    margin="normal"
                  />
                </GridItem>
                
                <GridItem xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="waterEC"
                    label="Water Base EC"
                    type="number"
                    value={formData.waterEC}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: <Typography variant="caption">mS/cm</Typography>
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    margin="normal"
                  />
                </GridItem>
                
                <GridItem xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="containerVolume"
                    label="Working Solution Volume"
                    type="number"
                    value={formData.containerVolume}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: <Typography variant="caption">L</Typography>
                    }}
                    inputProps={{ min: 0.1, step: 0.1 }}
                    margin="normal"
                  />
                </GridItem>
                
                {formData.calculationType === 'ratio' && (
                  <GridItem xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="stockSolutionVolume"
                      label="Stock Solution Available"
                      type="number"
                      value={formData.stockSolutionVolume}
                      onChange={handleInputChange}
                      InputProps={{
                        endAdornment: <Typography variant="caption">L</Typography>
                      }}
                      inputProps={{ min: 0.1, step: 0.1 }}
                      margin="normal"
                    />
                  </GridItem>
                )}
              </Grid>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleCalculate}
                >
                  Calculate
                </Button>
              </Box>
            </Paper>
          </GridItem>
          
          {result && (
            <GridItem xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                {result.error ? (
                  <Alert severity="error">{result.error}</Alert>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>Dilution Results</Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Grid container spacing={2}>
                        <GridItem xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Stock Solution Amount
                          </Typography>
                          <Typography variant="body1">
                            {result.stockSolutionAmount} mL
                          </Typography>
                        </GridItem>
                        
                        <GridItem xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Water Amount
                          </Typography>
                          <Typography variant="body1">
                            {result.waterAmount} L
                          </Typography>
                        </GridItem>
                        
                        <GridItem xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Final EC
                          </Typography>
                          <Typography variant="body1">
                            {result.finalEC} mS/cm
                          </Typography>
                        </GridItem>
                        
                        <GridItem xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            Dilution Ratio
                          </Typography>
                          <Typography variant="body1">
                            {result.dilutionRatio}
                          </Typography>
                        </GridItem>
                      </Grid>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle1" gutterBottom>How to Mix</Typography>
                    <Typography variant="body2" paragraph>
                      Add {result.stockSolutionAmount} mL of stock solution to your container, then fill with water to a total volume of {formData.containerVolume} L.
                    </Typography>
                    
                    <Alert severity="info">
                      Always add the stock solution first, then add water. Stir well to ensure uniform distribution.
                    </Alert>
                  </>
                )}
              </Paper>
            </GridItem>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default WorkingSolutionCalculator;
